import React, { useCallback, useMemo, useState } from 'react';
import {
  buildQueryFilters,
  buildSorter,
  useGetInfiniteOrganizationSummary,
} from '@sib/api';
import { DEFAULT_PAGE, MAXIMUM_PAGINATION } from '@sib/api/constants';
import { useTranslation } from '@sib/i18n';
import { CrudFilter } from '@sib/types';
import { AutocompleteIndicator, ComboboxAvataritemOption } from '@sib/ui';
import { useAutocompleteProps, useDebouncedValue } from '@sib/utils';
import { getInitials } from '../string-formatters';

const STALE_TIME_ORGANIZATIONS = 10 * 1000;

export interface UseOrganizationsAutocompletePropsOptions {
  additionalFields?: ComboboxAvataritemOption[];
  currentOrgData?: { id: string; name: string };
}

export const useOrganizationsAutocompleteProps = ({
  additionalFields: externalFields,
  currentOrgData,
}: UseOrganizationsAutocompletePropsOptions) => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState('');
  const debouncedFilter = useDebouncedValue(filter, 500);

  const apiFilter = useMemo<CrudFilter[]>(
    () =>
      debouncedFilter
        ? [
            {
              field: 'name',
              value: debouncedFilter,
              operator: 'contains' as const,
            },
          ]
        : [],
    [debouncedFilter]
  );

  const payload = useMemo(
    () => ({
      page: DEFAULT_PAGE,
      size: MAXIMUM_PAGINATION,
      sort: [buildSorter({ field: 'name', order: 'asc' })],
      ...(apiFilter.length && { filter: buildQueryFilters(apiFilter) }),
    }),
    [apiFilter]
  );

  const params = useGetInfiniteOrganizationSummary(payload, {
    staleTime: STALE_TIME_ORGANIZATIONS,
  });

  const additionalFields = useMemo<ComboboxAvataritemOption[]>(
    () =>
      [
        currentOrgData &&
          currentOrgData.name.toLowerCase().includes(filter.toLowerCase()) && {
            label: (
              <AutocompleteIndicator indicator={t('current')}>
                {currentOrgData.name}
              </AutocompleteIndicator>
            ),
            value: currentOrgData.id,
            initial: getInitials(currentOrgData.name),
          },
        ...(externalFields?.filter(i => i.value !== currentOrgData?.id) || []),
      ].filter(Boolean) as ComboboxAvataritemOption[],
    [externalFields, currentOrgData, filter, t]
  );

  const optionsMapper = useCallback(
    (org: {
      id: string;
      organizationName: string;
    }): ComboboxAvataritemOption => ({
      label: org.organizationName,
      value: org.id,
      initial: getInitials(org.organizationName),
    }),
    []
  );

  return useAutocompleteProps({
    params,
    filter,
    setFilter,
    optionsMapper,
    additionalFields,
  });
};
